/* eslint-disable no-console */
import React, { useState, useRef, useEffect, useCallback, memo } from 'react';
import { Button, OverlayTrigger, Tooltip, Spinner } from 'react-bootstrap';
import { createEditor, Editor, Transforms } from 'slate';
import { FaBold, FaItalic, FaUnderline } from 'react-icons/fa';
import { Slate, Editable, withReact, ReactEditor } from 'slate-react';
import { serialize, deserialize, extractText } from '../util/slateEditor';

const Element = ({ attributes, children, element }) => {
  const style = { textAlign: element.align };
  switch (element.type) {
    case 'block-quote':
      return (
        <blockquote style={style} {...attributes}>
          {children}
        </blockquote>
      );
    case 'bulleted-list':
      return (
        <ul style={style} {...attributes}>
          {children}
        </ul>
      );
    case 'heading-one':
      return (
        <h1 style={style} {...attributes}>
          {children}
        </h1>
      );
    case 'heading-two':
      return (
        <h2 style={style} {...attributes}>
          {children}
        </h2>
      );
    case 'list-item':
      return (
        <li style={style} {...attributes}>
          {children}
        </li>
      );
    case 'numbered-list':
      return (
        <ol style={style} {...attributes}>
          {children}
        </ol>
      );
    default:
      return (
        <p style={{ margin: 0, padding: 0 }} {...attributes}>
          {children}
        </p>
      );
  }
};

const Leaf = ({ attributes, children, leaf }) => {
  if (leaf.bold) children = <strong>{children}</strong>;
  if (leaf.code) children = <code>{children}</code>;
  if (leaf.italic) children = <em>{children}</em>;
  if (leaf.underline) children = <u>{children}</u>;
  return <span {...attributes}>{children}</span>;
};

const parseHtmlToSlate = html => {
  const { body } = new DOMParser().parseFromString(html ?? '', 'text/html');
  return Array.from(body?.childNodes ?? []).map(deserialize);
};

const RichTextEditor = memo(
  ({
    content,
    contentFooter,
    readOnly,
    saveContent = undefined,
    handleAttachment = undefined,
    imageUrl = undefined,
    appendContent = false,
  }) => {
    const renderElement = useCallback(props => <Element {...props} />, []);
    const renderLeaf = useCallback(props => <Leaf {...props} />, []);
    const [editor] = useState(() => withReact(createEditor()));
    const imageUrlRef = useRef(imageUrl);
    const menuItemsRef = useRef('');
    const [initialValue] = useState(() => parseHtmlToSlate(content));
    const valueRef = useRef(initialValue);
    const [activeMarks, setActiveMarks] = useState({});
    const [isPrinting, setIsPrinting] = useState(false);

    // Need this to remove content from previous editor when opening new report
    useEffect(() => {
      if (!appendContent) {
        const newValue = parseHtmlToSlate(content);

        Transforms.deselect(editor);

        // Clear the editor.
        if (editor.children.length > 0) {
          Editor.withoutNormalizing(editor, () => (editor.children = []));
        }

        Transforms.insertNodes(editor, newValue);

        // Reset the selection to the start of the document.
        if (editor.children.length > 0) {
          Transforms.select(editor, Editor.start(editor, []));
        } else {
          Transforms.insertNodes(editor, {
            type: 'paragraph',
            children: [{ text: '' }],
          });
        }
      }
    }, [content]);

    // Need this to add the content from Menu items
    useEffect(() => {
      if (appendContent) {
        // Parse the new content into a DOM.
        const nodes = parseHtmlToSlate(content);
        const textNodes = nodes.filter(node => 'text' in node);
        const newText = textNodes.map(node => node.text).join('');

        // Determine what text hasn't been inserted before.
        const itemToInsert = newText.replace(menuItemsRef.current, '');

        Transforms.insertText(editor, itemToInsert, {
          at: Editor.end(editor, []),
        });

        // Update the ref with the newly inserted text.
        menuItemsRef.current = newText;
      }
    }, [content, appendContent]);

    useEffect(() => {
      imageUrlRef.current = imageUrl;
    }, [imageUrl]);

    const toggleMark = (editor, format) => {
      const marks = Editor.marks(editor);
      const isActive = marks ? marks[format] === true : false;

      if (isActive) {
        Editor.removeMark(editor, format);
        setActiveMarks(prevMarks => ({ ...prevMarks, [format]: false }));
      } else {
        Editor.addMark(editor, format, true);
        setActiveMarks(prevMarks => ({ ...prevMarks, [format]: true }));
      }
    };

    const handleSave = () => {
      saveContent(serialize(valueRef.current));
    };

    const handlePrint = e => {
      setIsPrinting(true);
      e.preventDefault();

      const iframe = document.createElement('iframe');
      const img = new Image();
      let content = valueRef.current.map(extractText).join('');

      iframe.style.visibility = 'hidden';
      document.body.appendChild(iframe);

      const handleClosePrint = () => {
        if (iframe.parentNode) {
          iframe.parentNode.removeChild(iframe);
        }
        setIsPrinting(false);
      };

      if (imageUrlRef.current) {
        img.onload = () => {
          content = `
        <div style="margin-top: 80px;">

<div align="center">
    <!-- nomeIMG será substituído pelo caminho da imagem -->
    <img src="${img.src}" alt="ECG Imagem" width="720" height="800" />
</div>
<br>
<!-- DMRec.QECGObservacao.value será substituído pelo texto da observação -->
${content}
<br></br>
 ${contentFooter}

        </div>
        `;

          // Ensure iframe exists and is correctly referenced
          if (!iframe) {
            console.error('Iframe is not defined');
            handleClosePrint();
            return;
          }

          // Check if iframe is correctly attached to the DOM
          if (!document.body.contains(iframe)) {
            console.error('Iframe is not attached to the DOM');
            handleClosePrint();
            return;
          }

          iframe.contentDocument.open();
          iframe.contentDocument.write(content);
          iframe.contentDocument.close();

          // Add a delay to ensure content is rendered before printing
          setTimeout(() => {
            iframe.contentWindow.print();
            handleClosePrint();
          }, 500);
        };
        img.onerror = () => {
          console.error('Failed to load image for print');
          handleClosePrint();
        };
        img.src = imageUrlRef.current;
      } else {
        console.error('imageUrl prop is empty!');
        iframe.contentDocument.open();
        iframe.contentDocument.write(content);
        iframe.contentDocument.close();
        iframe.contentWindow.print();
        handleClosePrint();
      }
    };

    const triggerEvent = window.matchMedia('(hover: hover)').matches
      ? ['hover', 'focus']
      : ['focus'];

    return (
      <>
        <Slate
          editor={editor}
          initialValue={valueRef.current}
          onChange={newValue => {
            valueRef.current = newValue; // Update ref without triggering a rerender
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 10,
              margin: '10px',
              padding: 5,
              border: '1px solid #ccc',
            }}
          >
            {!readOnly && (
              <>
                <OverlayTrigger
                  trigger={triggerEvent}
                  overlay={<Tooltip id="tooltip-bold">Negrito</Tooltip>}
                >
                  <FaBold
                    cursor="pointer"
                    onClick={event => {
                      event.preventDefault();
                      toggleMark(editor, 'bold');
                      ReactEditor.focus(editor);
                    }}
                    style={{ color: activeMarks['bold'] ? 'black' : '#ccc' }}
                  />
                </OverlayTrigger>
                <OverlayTrigger
                  trigger={triggerEvent}
                  overlay={<Tooltip id="tooltip-italic">Itálico</Tooltip>}
                >
                  <FaItalic
                    cursor="pointer"
                    onClick={event => {
                      event.preventDefault();
                      toggleMark(editor, 'italic');
                      ReactEditor.focus(editor);
                    }}
                    style={{ color: activeMarks['italic'] ? 'black' : '#ccc' }}
                  />
                </OverlayTrigger>
                <OverlayTrigger
                  trigger={triggerEvent}
                  overlay={<Tooltip id="tooltip-underline">Underline</Tooltip>}
                >
                  <FaUnderline
                    cursor="pointer"
                    onClick={event => {
                      event.preventDefault();
                      toggleMark(editor, 'underline');
                      ReactEditor.focus(editor);
                    }}
                    style={{
                      color: activeMarks['underline'] ? 'black' : '#ccc',
                    }}
                  />
                </OverlayTrigger>
              </>
            )}
            {handleAttachment && (
              <Button onClick={handleAttachment} variant="warning">
                Anexar
              </Button>
            )}
            <Button onClick={handlePrint} disabled={isPrinting}>
              {isPrinting ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />{' '}
                  Preparando...
                </>
              ) : (
                'Imprimir'
              )}
            </Button>
            {!readOnly && (
              <Button onClick={handleSave} variant="success">
                Salvar
              </Button>
            )}
          </div>

          <Editable
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            readOnly={readOnly}
            autoFocus
            style={{
              padding: '10px',
              margin: '10px',
              border: '1px solid #ccc',
              borderRadius: '5px',
              minHeight: '200px',
              maxHeight: '500px',
              overflowY: 'auto',
            }}
          />
        </Slate>
      </>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison function for better control
    return (
      prevProps.content === nextProps.content &&
      prevProps.contentFooter === nextProps.contentFooter &&
      prevProps.imageUrl === nextProps.imageUrl &&
      prevProps.appendContent === nextProps.appendContent &&
      prevProps.readOnly === nextProps.readOnly &&
      prevProps.saveContent === nextProps.saveContent &&
      prevProps.handleAttachment === nextProps.handleAttachment
    );
  }
);

export default RichTextEditor;
